import { useCallback } from 'react';

import { largeUploadWithS3InfoByIdSelector, type Upload } from '@air/redux-uploader';
import { useUploadLargeFileChunkWithRetries } from '@air/upload-utils';

import { type PrivateUploadTaskParams } from '~/components/Upload/hooks/types';
import { useLargeUploadPartTrackingHandlers } from '~/components/Upload/hooks/useLargeUploadPartTrackingHandlers';
import { getPrivateUploadAnalyticsParams } from '~/components/Upload/utils/uploadAnalytics';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { privateUploadBoardIdSelector } from '~/store/privateUploadMetadata/selectors';
import { useAirStore } from '~/utils/ReduxUtils';
import { fetchPrivatePartUploadUrl } from '~/utils/uploadApi/privateUploadApi/privateLargeUploadApi';

export const useUploadLargePrivateFileWithRetries = () => {
  const store = useAirStore();
  const { uploadLargeFileChunkWithRetries } = useUploadLargeFileChunkWithRetries();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { getPartTrackingHandlers } = useLargeUploadPartTrackingHandlers();

  const uploadLargePrivateFileWithRetries = useCallback(
    ({
      uploadId,
      chunk,
      onError,
      partNumber,
    }: {
      uploadId: Upload['id'];
      partNumber: number;
      chunk: Blob;
      onError: PrivateUploadTaskParams['onError'];
    }) => {
      const upload = largeUploadWithS3InfoByIdSelector(store.getState(), uploadId);

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      if (upload) {
        return uploadLargeFileChunkWithRetries({
          chunk,
          upload,
          partNumber,
          ...getPartTrackingHandlers({
            getAnalyticsParams: (upload) =>
              getPrivateUploadAnalyticsParams({
                context: 'private_large_part_upload',
                state: store.getState(),
                upload,
              }),
            onError,
          }),
          getPartUrl: async (partNumber) => {
            const parentBoardId = privateUploadBoardIdSelector(store.getState(), upload.id);

            return fetchPrivatePartUploadUrl({
              partNumber,
              s3Info: upload.s3Info.uploadUrlInfo,
              parentBoardId,
              workspaceId,
            });
          },
        });
      }
    },
    [getPartTrackingHandlers, store, uploadLargeFileChunkWithRetries, workspaceId],
  );

  return {
    uploadLargePrivateFileWithRetries,
  };
};
